import { shouldMakeAPICall, isOnline } from '../utils/network';
import { prRepository } from '../repositories/pr.repository';
import { masterDataRepository } from '../repositories/master-data.repository';
import { rfqRepository } from '../repositories/rfq.repository';
import { offlineQueue } from '../queue/offlineQueue';

/**
 * Sync Engine
 * Handles automatic synchronization of all data
 */
export class SyncEngine {
  constructor() {
    this.isSyncing = false;
    this.syncInterval = null;
  }

  /**
   * Sync all initial load data
   */
  async syncAllInitialData(authToken, userToken, orgId) {
    if (!shouldMakeAPICall(authToken, userToken)) {
      console.warn('⚠️ Sync skipped - offline or not authenticated');
      return;
    }

    if (this.isSyncing) {
      console.warn('⚠️ Sync already in progress');
      return;
    }

    this.isSyncing = true;

    try {
      console.log('🔄 Starting full data sync...');

      // Sync all data in parallel
      const syncResults = await Promise.allSettled([
        prRepository.syncCombinedPRsFromAPI(authToken, userToken, orgId).catch(() => 
          prRepository.syncFromAPI(authToken, userToken, orgId)
        ),
        // RFQ main list (buyer_rfq_full_view) - cache on initial load
        // userToken here is loginData.user.token (same as org_token/buyer_token used by RFQ screens)
        // null = API returns all RFQs in one call
        rfqRepository.syncFromAPI(authToken, userToken, null, '', '', '', '', '').then(result => {
          console.log(`✅ RFQ sync completed: ${result?.length || 0} RFQs cached`);
          return result;
        }).catch(err => {
          console.error('❌ RFQ sync failed:', err);
          throw err;
        }),
        masterDataRepository.syncCostCenters(authToken, userToken),
        masterDataRepository.syncGLCodes(authToken, userToken),
        masterDataRepository.syncProjectCodes(authToken, userToken),
        masterDataRepository.syncItems(authToken, userToken),
        masterDataRepository.syncDepartments(authToken, userToken),
        masterDataRepository.syncVendors(authToken, userToken),
        masterDataRepository.syncUsers(authToken, userToken),
        masterDataRepository.syncPRCodes(authToken),
        // RFQ Master Data
        masterDataRepository.syncCountries(authToken),
        masterDataRepository.syncStates(authToken, null), // Load all states
        masterDataRepository.syncCities(authToken, null), // Load all cities
        masterDataRepository.syncRFQCategories(authToken, userToken),
        masterDataRepository.syncShippingAddresses(authToken, userToken),
      ]);
      
      // Log RFQ sync result specifically
      const rfqSyncResult = syncResults[1];
      if (rfqSyncResult.status === 'fulfilled') {
        console.log(`✅ RFQ initial load: ${rfqSyncResult.value?.length || 0} RFQs cached`);
      } else {
        console.error('❌ RFQ initial load failed:', rfqSyncResult.reason);
      }

      console.log('✅ Full data sync completed');
    } catch (error) {
      console.error('❌ Error during sync:', error);
    } finally {
      this.isSyncing = false;
    }
  }

  /**
   * Sync offline queue
   */
  async syncQueue(authToken, userToken, orgId) {
    if (!shouldMakeAPICall(authToken, userToken)) {
      return;
    }

    try {
      const pendingActions = await offlineQueue.getPending();
      
      if (pendingActions.length === 0) {
        return;
      }

      console.log(`🔄 Syncing ${pendingActions.length} queued actions...`);

      for (const action of pendingActions) {
        console.log(`🔄 Processing queued action: ${action.action} for ${action.endpoint}`);
        console.log('Action payload keys:', Object.keys(action.payload || {}));
        
        const success = await offlineQueue.processAction(action, authToken, userToken);
        
        if (success) {
          console.log(`✅ Action ${action.action} completed successfully`);
          
          // If PR update/create was successful, refresh PR list
          if (action.endpoint.includes('ins_purchase_request')) {
            console.log('✅ PR action completed, refreshing PR list...');
            // Refresh PR list to get updated data from server
            await prRepository.syncCombinedPRsFromAPI(authToken, userToken, orgId).catch(() => 
              prRepository.syncFromAPI(authToken, userToken, orgId)
            );
          }
          
          // If RFQ update/create/delete was successful, refresh RFQ list
          if (action.endpoint.includes('multiple_item_rfq_update') || 
              action.endpoint.includes('add_rfq_multiple_item') || 
              action.endpoint.includes('rfq_delete')) {
            console.log('✅ RFQ action completed, refreshing RFQ list...');
            // Refresh RFQ list to get updated data from server (API returns all RFQs in one call)
            await rfqRepository.syncFromAPI(
              authToken,
              orgId, // orgId is buyer_token for RFQ
              null, // null = get all RFQs (API returns all in one call)
              '', // No search filter
              '', // No date filters
              '', // No date filters
              '', // No sorting
              ''  // No sorting
            );
            console.log('✅ RFQ list refreshed after sync');
          }
        } else {
          console.warn(`⚠️ Action ${action.action} failed, will retry later`);
        }
        
        // Small delay between actions
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      console.log('✅ Queue sync completed');
    } catch (error) {
      console.error('❌ Error syncing queue:', error);
    }
  }

  /**
   * Start automatic sync
   */
  startAutoSync(authToken, userToken, orgId, intervalMs = 30000) {
    if (this.syncInterval) {
      this.stopAutoSync();
    }

    this.syncInterval = setInterval(async () => {
      if (isOnline() && shouldMakeAPICall(authToken, userToken)) {
        await this.syncQueue(authToken, userToken, orgId);
      }
    }, intervalMs);

    console.log('✅ Auto-sync started');
  }

  /**
   * Stop automatic sync
   */
  stopAutoSync() {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
      console.log('⏹️ Auto-sync stopped');
    }
  }

  /**
   * Manual sync trigger
   */
  async manualSync(authToken, userToken, orgId) {
    await this.syncAllInitialData(authToken, userToken, orgId);
    await this.syncQueue(authToken, userToken, orgId);
  }

  /**
   * Sync on network reconnect
   */
  async syncOnReconnect(authToken, userToken, orgId) {
    if (isOnline()) {
      console.log('🌐 Network reconnected, syncing...');
      
      // Always try to sync queue first (user actions are priority)
      console.log('🔄 Priority: Syncing queued actions first...');
      await this.syncQueue(authToken, userToken, orgId);
      
    }
  }
}

export const syncEngine = new SyncEngine();
